import type {
  ChronologyEvent,
  CreateChronologyEventRequest,
  UpdateChronologyEventRequest,
} from "~/api/model";
import { DatePrecision } from "~/api/model";
import { dayjs, formatDateOnly } from "~/utils/date";

const ISO_DATE_FORMAT = "YYYY-MM-DD";

/**
 * The editable surface of a chronology event. Dates are held as `Date | null`
 * so the form can hand them straight to the shared DayPicker; everything else
 * is a trimmed string ("" meaning "no value").
 */
export interface ChronologyFormValues {
  eventDate: Date | null;
  detail: string;
  eventType: string;
  provider: string;
  facility: string;
  documentCategory: string;
  providerSpecialty: string;
  sourcePage: string;
  batesNumber: string;
  /** When set, the backend derives sourcePage/batesNumber from this page link. */
  pageId?: string;
}

export const EMPTY_CHRONOLOGY_FORM_VALUES: ChronologyFormValues = {
  eventDate: null,
  detail: "",
  eventType: "",
  provider: "",
  facility: "",
  documentCategory: "",
  providerSpecialty: "",
  sourcePage: "",
  batesNumber: "",
  pageId: undefined,
};

/** Parses a stored YYYY-MM-DD value into a local Date for the date picker. */
export const parseEventDate = (eventDate: string | null): Date | null => {
  if (!eventDate) return null;
  const [year, month, day] = eventDate.split("T")[0].split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day, 12, 0, 0);
};

/** Serializes a picker Date back to the YYYY-MM-DD the API expects. */
export const formatEventDate = (date: Date | null): string | null => {
  if (!date) return null;
  return dayjs(date).format(ISO_DATE_FORMAT);
};

/**
 * Formats an event's date for display, honoring the extracted precision so we
 * never imply more certainty than we have. A day-precise date shows the full
 * MM/DD/YYYY; coarser precisions prefer the raw extracted `dateText`, falling
 * back to a month- or year-only format when no text is available.
 */
export const formatChronologyEventDate = (
  event: Pick<ChronologyEvent, "eventDate" | "datePrecision" | "dateText">
): string | null => {
  const dateText = event.dateText.trim();

  if (event.datePrecision === DatePrecision.DAY && event.eventDate) {
    return formatDateOnly(event.eventDate);
  }

  if (dateText.length > 0) {
    return dateText;
  }

  if (event.eventDate) {
    if (event.datePrecision === DatePrecision.MONTH) {
      return formatDateOnly(event.eventDate, "MMM YYYY");
    }
    if (event.datePrecision === DatePrecision.YEAR) {
      return formatDateOnly(event.eventDate, "YYYY");
    }
    return formatDateOnly(event.eventDate);
  }

  return null;
};

/** Builds the editor's initial values from an event's current (effective) values. */
export const eventToFormValues = (
  event: ChronologyEvent
): ChronologyFormValues => ({
  eventDate: parseEventDate(event.eventDate),
  detail: event.detail,
  eventType: event.eventType,
  provider: event.provider,
  facility: event.facility,
  documentCategory: event.documentCategory,
  providerSpecialty: event.providerSpecialty,
  sourcePage:
    event.reference.pageNumber != null
      ? String(event.reference.pageNumber + 1)
      : "",
  batesNumber: event.reference.batesNumber ?? "",
});

const trimToValue = (value: string): string | undefined => {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const parsePage = (value: string): number | undefined => {
  const trimmed = value.trim();
  if (trimmed.length === 0) return undefined;
  const parsed = Number(trimmed);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
};

const formPageToApiPage = (value: string): number | undefined => {
  return parsePage(value);
};

/** Maps form values to the create payload, dropping empty optional fields. */
export const formValuesToCreateRequest = (
  values: ChronologyFormValues
): CreateChronologyEventRequest => {
  const request: CreateChronologyEventRequest = {
    detail: values.detail.trim(),
  };

  const eventDate = formatEventDate(values.eventDate);
  if (eventDate) request.eventDate = eventDate;

  const eventType = trimToValue(values.eventType);
  if (eventType) request.eventType = eventType;

  const provider = trimToValue(values.provider);
  if (provider) request.provider = provider;

  const facility = trimToValue(values.facility);
  if (facility) request.facility = facility;

  const documentCategory = trimToValue(values.documentCategory);
  if (documentCategory) request.documentCategory = documentCategory;

  const providerSpecialty = trimToValue(values.providerSpecialty);
  if (providerSpecialty) request.providerSpecialty = providerSpecialty;

  if (values.pageId) {
    request.pageId = values.pageId;
  } else {
    const sourcePage = formPageToApiPage(values.sourcePage);
    if (sourcePage != null) request.sourcePage = sourcePage;

    const batesNumber = trimToValue(values.batesNumber);
    if (batesNumber) request.batesNumber = batesNumber;
  }

  return request;
};

/**
 * Diffs the edited form values against the event's current values and returns
 * a patch containing only the changed fields. An emptied field is sent as
 * `null` so the override is blanked/cleared rather than left untouched. This
 * avoids writing spurious overrides for fields the reviewer never touched.
 */
export const formValuesToUpdateRequest = (
  event: ChronologyEvent,
  values: ChronologyFormValues
): UpdateChronologyEventRequest => {
  const request: UpdateChronologyEventRequest = {};

  const nextEventDate = formatEventDate(values.eventDate);
  if (nextEventDate !== event.eventDate) {
    request.eventDate = nextEventDate;
  }

  const stringFields: {
    key:
      | "detail"
      | "eventType"
      | "provider"
      | "facility"
      | "documentCategory"
      | "providerSpecialty";
    next: string;
    current: string;
  }[] = [
    { key: "detail", next: values.detail, current: event.detail },
    { key: "eventType", next: values.eventType, current: event.eventType },
    { key: "provider", next: values.provider, current: event.provider },
    { key: "facility", next: values.facility, current: event.facility },
    {
      key: "documentCategory",
      next: values.documentCategory,
      current: event.documentCategory,
    },
    {
      key: "providerSpecialty",
      next: values.providerSpecialty,
      current: event.providerSpecialty,
    },
  ];

  for (const { key, next, current } of stringFields) {
    const trimmed = next.trim();
    if (trimmed !== current) {
      request[key] = trimmed.length > 0 ? trimmed : null;
    }
  }

  const currentPage =
    event.reference.pageNumber != null ? event.reference.pageNumber + 1 : null;
  const nextPage = formPageToApiPage(values.sourcePage) ?? null;
  if (nextPage !== currentPage) {
    request.sourcePage = nextPage;
  }

  const nextBates = trimToValue(values.batesNumber) ?? null;
  if (nextBates !== event.reference.batesNumber) {
    request.batesNumber = nextBates;
  }

  return request;
};
